/** Domain primitives: RatingStars, LevelBadge, PriceBlock.
 *  These are NVL-specific (used by every course/product/service card) and live here so
 *  that catalog pages can compose them quickly when feature prompts arrive.
 */
import { Star } from "lucide-react";
import { useCurrency } from "../../contexts/CurrencyContext";
import { cn } from "../../lib/cn";

export function RatingStars({ value = 0, size = 13, count, className }) {
  return (
    <span
      className={cn("inline-flex items-center gap-1.5 text-text-dim text-[12.5px]", className)}
      data-testid="rating-stars"
    >
      <Star size={size} className="text-gold fill-gold" strokeWidth={0} />
      <b className="text-text font-semibold">{Number(value).toFixed(1)}</b>
      {count !== undefined && <span className="text-text-faint">({count})</span>}
    </span>
  );
}

const LEVELS = ["Foundational", "Intermediate", "Advanced"];

export function LevelBadge({ level = 1, className }) {
  const n = Math.max(1, Math.min(3, level));
  return (
    <span
      className={cn(
        "inline-flex items-center gap-2 text-[11.5px] font-mono uppercase tracking-[0.14em] text-text-dim",
        className
      )}
      data-testid="level-badge"
    >
      <span className="inline-flex items-center gap-[3px]">
        {[0, 1, 2].map((i) => (
          <span
            key={i}
            className={cn(
              "block w-[5px] h-[5px] rounded-full",
              i < n ? "bg-mauve" : "bg-line-2"
            )}
          />
        ))}
      </span>
      {LEVELS[n - 1]}
    </span>
  );
}

export function PriceBlock({ price, was, className, size = "default" }) {
  const { format } = useCurrency();
  const sizes = {
    default: { now: "text-[20px]", was: "text-[13px]" },
    sm: { now: "text-[16px]", was: "text-[12px]" },
    lg: { now: "text-[28px]", was: "text-[14px]" },
  };
  const s = sizes[size];
  return (
    <div className={cn("flex items-baseline gap-2.5", className)} data-testid="price-block">
      <span className={cn("font-mono text-text font-medium tracking-[-0.02em] tabular-nums", s.now)} data-testid="price-now">
        {format(price)}
      </span>
      {was > 0 && (
        <span className={cn("text-text-faint line-through font-mono tabular-nums", s.was)} data-testid="price-was">{format(was)}</span>
      )}
    </div>
  );
}
